// Client typé de toute l'API Feedbaacks. Trois façons de s'authentifier :
//   - session : navigateur, clé publique + identité signée par le serveur de
//     l'app (ou visiteur anonyme si l'app l'autorise), jeton renouvelé seul ;
//   - secret  : serveur à serveur, clé sk_ + la personne réelle (en-tête
//     X-Feedback-External-Author), comme Academy et caast_api ;
//   - token   : jeton du tableau de suivi (connexion par code), app choisie.
import { base64UrlEncode } from './encoding'
import { streamSse } from './sse'
import type {
  AgentStatus, AppDetail, AppSettings, AppSummary, Author, Comment, Counts, CreateInput, EmailCounts, EmailDetail,
  EmailOutboxPage, EmailStatus, FeedbackItem, FeedbackStatus, ListQuery, Me, Media, Member, MemberRole, Mention, Page,
  Person, RealtimeEvent, SecretKey, Session, SimilarItem, TowerTarget, UpdateInput, VoteResult,
} from './types'
import { towerApi } from './tower'

export const EXTERNAL_AUTHOR_HEADER = 'X-Feedback-External-Author'
export const SOURCE_HEADER = 'X-Feedback-Source'
export const APP_HEADER = 'X-Feedbaacks-App'

/** Identité signée fournie par le serveur de l'app hôte. */
export interface SignedIdentity {
  user: Person
  /** hex(HMAC-SHA256(identity_secret, id + "\n" + email en minuscules)) */
  signature: string
}

export type Auth =
  | {
      type: 'session'
      publishableKey: string
      /** Identité signée, ou fonction qui la (re)demande au serveur hôte. */
      identity?: SignedIdentity | (() => Promise<SignedIdentity>)
      /** Visiteur anonyme (l'app doit l'autoriser). */
      anonymous?: boolean
    }
  | { type: 'secret'; secretKey: string; actor: Person; source?: string }
  | { type: 'token'; token: string; app?: string }

export interface ClientOptions {
  /** URL de l'API, sans /api (ex. https://feedbaacks.caast.tv). */
  baseUrl: string
  auth: Auth
  fetch?: typeof fetch
  /** Stockage de l'identifiant anonyme (localStorage par défaut). */
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null
  /** Marge avant expiration à laquelle la session est renouvelée. */
  renewMarginMs?: number
}

/** Erreur de l'API, forme {error:{code,message}}. */
/** Personne n'est connecté chez l'hôte (aucune identité, anonymes refusés). */
export const IDENTITY_REQUIRED = 'identity_required'

export class FeedbaacksError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message)
    this.name = 'FeedbaacksError'
  }
}

type Query = Record<string, string | number | boolean | undefined | null | string[]>

interface RequestOptions {
  body?: unknown
  query?: Query
  form?: FormData
  signal?: AbortSignal
  /** Réponse binaire (image) : rendue en Blob. */
  blob?: boolean
}

const ANON_KEY = 'feedbaacks:anonymous:'

function randomUUID(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
  const b = new Uint8Array(16)
  globalThis.crypto.getRandomValues(b)
  b[6] = (b[6] & 0x0f) | 0x40
  b[8] = (b[8] & 0x3f) | 0x80
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

function defaultStorage(): ClientOptions['storage'] {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

export function buildQuery(q: Query = {}): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === false || v === '') continue
    if (Array.isArray(v)) {
      if (v.length) p.set(k, v.join(','))
    } else p.set(k, String(v))
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

export class FeedbaacksClient {
  readonly baseUrl: string
  private auth: Auth
  private readonly fetcher: typeof fetch
  private readonly storage: ClientOptions['storage']
  private readonly renewMargin: number
  private session: Session | null = null
  private sessionPromise: Promise<Session> | null = null
  private memoryAnonId: string | null = null

  constructor(opts: ClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/+$/, '')
    this.auth = opts.auth
    this.fetcher = opts.fetch ?? ((...a) => globalThis.fetch(...a))
    this.storage = opts.storage === undefined ? defaultStorage() : opts.storage
    this.renewMargin = opts.renewMarginMs ?? 5 * 60_000
  }

  /** L'app lue par un jeton du tableau de suivi. */
  setApp(app: string | undefined): void {
    if (this.auth.type === 'token') this.auth = { ...this.auth, app }
  }

  /** La voie d'authentification : 'session' (navigateur), 'secret', 'token'. */
  get authType(): Auth['type'] {
    return this.auth.type
  }

  get app(): string | undefined {
    if (this.auth.type === 'token') return this.auth.app
    return this.session?.app.key
  }

  /** La session courante (voie navigateur), ouverte au besoin. */
  async currentSession(): Promise<Session> {
    if (this.auth.type !== 'session') throw new Error('session réservée à la voie navigateur')
    const s = this.session
    if (s && new Date(s.expires_at).getTime() - Date.now() > this.renewMargin) return s
    this.sessionPromise ??= this.openSession().finally(() => {
      this.sessionPromise = null
    })
    return this.sessionPromise
  }

  private anonymousId(): string {
    if (this.auth.type !== 'session') return ''
    const key = ANON_KEY + this.auth.publishableKey
    try {
      const existing = this.storage?.getItem(key)
      if (existing) return existing
      const id = randomUUID()
      this.storage?.setItem(key, id)
      return id
    } catch {
      this.memoryAnonId ??= randomUUID()
      return this.memoryAnonId
    }
  }

  private async openSession(): Promise<Session> {
    if (this.auth.type !== 'session') throw new Error('voie navigateur attendue')
    const body: Record<string, unknown> = { publishable_key: this.auth.publishableKey }
    const id = this.auth.identity
    if (id) {
      // L'hôte ne sait pas dire qui est connecté (personne, ou session
      // expirée chez lui) : même code que le refus du serveur.
      const signed = typeof id === 'function'
        ? await id().catch((err: unknown) => {
            throw new FeedbaacksError(401, IDENTITY_REQUIRED, err instanceof Error ? err.message : String(err))
          })
        : id
      body.user = signed.user
      body.signature = signed.signature
    } else {
      body.anonymous_id = this.anonymousId()
    }
    const s = await this.raw<Session>('POST', '/api/session', { body }, false)
    this.session = s
    return s
  }

  /** En-têtes d'authentification de la voie courante. */
  async authHeaders(): Promise<Record<string, string>> {
    const a = this.auth
    switch (a.type) {
      case 'secret': {
        const h: Record<string, string> = {
          Authorization: `Bearer ${a.secretKey}`,
          [EXTERNAL_AUTHOR_HEADER]: base64UrlEncode(JSON.stringify(a.actor)),
        }
        if (a.source) h[SOURCE_HEADER] = a.source
        return h
      }
      case 'token': {
        const h: Record<string, string> = { Authorization: `Bearer ${a.token}` }
        if (a.app) h[APP_HEADER] = a.app
        return h
      }
      case 'session':
        return { Authorization: `Bearer ${(await this.currentSession()).token}` }
    }
  }

  /** Requête authentifiée ; une session refusée est rouverte une fois. */
  async request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
    try {
      return await this.raw<T>(method, path, opts, true)
    } catch (err) {
      if (err instanceof FeedbaacksError && err.status === 401 && this.auth.type === 'session') {
        this.session = null
        return this.raw<T>(method, path, opts, true)
      }
      throw err
    }
  }

  private async raw<T>(method: string, path: string, opts: RequestOptions, authed: boolean): Promise<T> {
    const headers: Record<string, string> = authed ? await this.authHeaders() : {}
    let body: BodyInit | undefined
    if (opts.form) body = opts.form
    else if (opts.body !== undefined) {
      headers['Content-Type'] = 'application/json'
      body = JSON.stringify(opts.body)
    }
    const res = await this.fetcher(`${this.baseUrl}${path}${buildQuery(opts.query)}`, {
      method, headers, body, signal: opts.signal,
    })
    if (res.status === 204) return undefined as T
    if (opts.blob && res.ok) return (await res.blob()) as T
    const text = await res.text()
    let json: unknown = undefined
    if (text) {
      try {
        json = JSON.parse(text)
      } catch {
        json = undefined
      }
    }
    if (!res.ok) {
      const e = (json as { error?: { code?: string; message?: string } } | undefined)?.error
      throw new FeedbaacksError(res.status, e?.code ?? 'http_' + res.status, e?.message ?? (text || res.statusText))
    }
    return json as T
  }

  // ── Retours ───────────────────────────────────────────────────────────

  readonly feedback = {
    list: (q: ListQuery = {}, signal?: AbortSignal) =>
      this.request<Page<FeedbackItem>>('GET', '/api/feedback', { query: q as Query, signal }),
    counts: () => this.request<Counts>('GET', '/api/feedback/counts'),
    get: (id: string) => this.request<FeedbackItem>('GET', `/api/feedback/${enc(id)}`),
    create: (input: CreateInput) => this.request<FeedbackItem>('POST', '/api/feedback', { body: input }),
    update: (id: string, input: UpdateInput) =>
      this.request<FeedbackItem>('PATCH', `/api/feedback/${enc(id)}`, { body: input }),
    remove: (id: string) => this.request<void>('DELETE', `/api/feedback/${enc(id)}`),
    setStatus: (id: string, status: FeedbackStatus, duplicateOf?: string) =>
      this.request<FeedbackItem>('PATCH', `/api/feedback/${enc(id)}/status`, {
        body: { status, duplicate_of: duplicateOf },
      }),
    setAgentApproval: (id: string, approved: boolean) =>
      this.request<FeedbackItem>('PATCH', `/api/feedback/${enc(id)}/agent-approval`, { body: { approved } }),
    /** Projets de la tour proposés à « À traiter par l'agent » (admin de l'app). */
    towerProjects: () => this.request<{ data: TowerTarget[] }>('GET', '/api/feedback/tower-projects').then((r) => r.data),
    /** Envoie le retour au tri de la tour, vers ce projet (au lieu de la ferme d'agents). */
    sendToTower: (id: string, project: string) =>
      this.request<FeedbackItem>('POST', `/api/feedback/${enc(id)}/tower`, { body: { project } }),
    archive: (id: string) => this.request<FeedbackItem>('POST', `/api/feedback/${enc(id)}/archive`),
    unarchive: (id: string) => this.request<FeedbackItem>('POST', `/api/feedback/${enc(id)}/unarchive`),
    vote: (id: string, value: -1 | 0 | 1) =>
      this.request<VoteResult>('POST', `/api/feedback/${enc(id)}/vote`, { body: { value } }),
    markRead: (id: string) => this.request<void>('POST', `/api/feedback/${enc(id)}/mark-read`),
    searchSimilar: (q: string) =>
      this.request<{ data: SimilarItem[] }>('GET', '/api/feedback/search-similar', { query: { q } }).then((r) => r.data),
    unreadCount: () => this.request<{ count: number }>('GET', '/api/feedback/unread-count').then((r) => r.count),
    uploadScreenshot: (file: Blob, filename = 'capture.png') => {
      const form = new FormData()
      form.append('file', file, filename)
      return this.request<{ url: string }>('POST', '/api/feedback/screenshot', { form }).then((r) => r.url)
    },
    uploadMedia: (file: Blob, filename = (file as File).name || 'fichier') => {
      const form = new FormData()
      form.append('file', file, filename)
      return this.request<Media>('POST', '/api/feedback/media', { form })
    },
    claim: (id: string, holder: string, ttlMinutes?: number) =>
      this.request<{ id: string; holder: string; expires_at: string }>('POST', `/api/feedback/${enc(id)}/claim`, {
        body: { holder, ttl_minutes: ttlMinutes },
      }),
    release: (id: string, holder: string) =>
      this.request<{ id: string; released: boolean }>('DELETE', `/api/feedback/${enc(id)}/claim`, { query: { holder } }),
  }

  readonly comments = {
    list: (itemId: string, q: { limit?: number; offset?: number } = {}) =>
      this.request<Page<Comment>>('GET', `/api/feedback/${enc(itemId)}/comments`, { query: q }),
    create: (itemId: string, body: string, opts: { parentId?: string; attachments?: Media[]; mentions?: Mention[] } = {}) =>
      this.request<Comment>('POST', `/api/feedback/${enc(itemId)}/comments`, {
        body: { body, parent_id: opts.parentId, attachments: opts.attachments, mentions: opts.mentions },
      }),
    update: (itemId: string, commentId: string, body: string) =>
      this.request<Comment>('PATCH', `/api/feedback/${enc(itemId)}/comments/${enc(commentId)}`, { body: { body } }),
    remove: (itemId: string, commentId: string) =>
      this.request<void>('DELETE', `/api/feedback/${enc(itemId)}/comments/${enc(commentId)}`),
    /** Pose la réaction, ou la retire si elle y est déjà. */
    react: (itemId: string, commentId: string, emoji: string) =>
      this.request<Comment>('POST', `/api/feedback/${enc(itemId)}/comments/${enc(commentId)}/reactions`, { body: { emoji } }),
    /** « En train d'écrire » : éphémère, rien n'est enregistré. */
    typing: (itemId: string) => this.request<void>('POST', `/api/feedback/${enc(itemId)}/typing`),
    /** Les personnes qu'on peut mentionner sur ce retour. */
    mentionable: (itemId: string, q = '') =>
      this.request<Page<Author>>('GET', `/api/feedback/${enc(itemId)}/mentionable`, { query: q ? { q } : {} }),
  }

  readonly agent = {
    status: () => this.request<AgentStatus>('GET', '/api/feedback-agent/status'),
    setStatus: (s: Partial<AgentStatus> & { state: AgentStatus['state'] }) =>
      this.request<AgentStatus>('POST', '/api/feedback-agent/status', { body: s }),
    queue: (holder?: string) =>
      this.request<{ data: FeedbackItem[]; count: number }>('GET', '/api/feedback-agent/queue', { query: { holder } }),
  }

  /** La tour de contrôle (équipe) : tri, tickets, agents, dispatchers. */
  readonly tower = towerApi(this)

  // ── Compte et tableau de suivi ───────────────────────────────────────

  readonly account = {
    me: () => this.request<Me>('GET', '/api/me'),
    setEmailDigest: (enabled: boolean) =>
      this.request<{ notification_preferences: Record<string, unknown> }>('PATCH', '/api/me/preferences', {
        body: { email_on_feedback_digest: enabled },
      }),
    /** Demande un code de connexion (même réponse si l'adresse est inconnue). */
    requestCode: (email: string) => this.raw<{ sent: boolean }>('POST', '/api/auth/code', { body: { email } }, false),
    verifyCode: (email: string, code: string) =>
      this.raw<{ token: string; expires_at: string }>('POST', '/api/auth/verify', { body: { email, code } }, false),
  }

  readonly apps = {
    list: () => this.request<{ data: AppSummary[] }>('GET', '/api/apps').then((r) => r.data),
    get: (key: string) => this.request<AppDetail>('GET', `/api/apps/${enc(key)}`),
    create: (input: { key: string; name: string; allowed_origins?: string[]; settings?: Partial<AppSettings> }) =>
      this.request<{ app: AppDetail; secret_key: string }>('POST', '/api/apps', { body: input }),
    update: (key: string, input: { name?: string; allowed_origins?: string[]; settings?: Partial<AppSettings> }) =>
      this.request<AppDetail>('PATCH', `/api/apps/${enc(key)}`, { body: input }),
    rotatePublicKeys: (key: string) => this.request<AppDetail>('POST', `/api/apps/${enc(key)}/rotate-public`),
    secretKeys: {
      list: (key: string) => this.request<{ data: SecretKey[] }>('GET', `/api/apps/${enc(key)}/secret-keys`).then((r) => r.data),
      create: (key: string, label: string) =>
        this.request<{ key: SecretKey; secret_key: string }>('POST', `/api/apps/${enc(key)}/secret-keys`, { body: { label } }),
      revoke: (key: string, id: string) => this.request<void>('DELETE', `/api/apps/${enc(key)}/secret-keys/${enc(id)}`),
    },
    members: {
      list: (key: string) => this.request<{ data: Member[] }>('GET', `/api/apps/${enc(key)}/members`).then((r) => r.data),
      set: (key: string, input: { email?: string; name?: string; user_id?: string; role: MemberRole }) =>
        this.request<{ user: { id: string; name: string }; role: MemberRole }>('PUT', `/api/apps/${enc(key)}/members`, { body: input }),
      remove: (key: string, userId: string) => this.request<void>('DELETE', `/api/apps/${enc(key)}/members/${enc(userId)}`),
    },
  }

  /** Administration (super admins) : la file de validation des e-mails. */
  readonly admin = {
    emailOutbox: {
      list: (q: { status?: EmailStatus; limit?: number } = {}) =>
        this.request<EmailOutboxPage>('GET', '/api/admin/email-outbox', { query: q }),
      counts: () => this.request<EmailCounts>('GET', '/api/admin/email-outbox/counts'),
      get: (id: string) => this.request<EmailDetail>('GET', `/api/admin/email-outbox/${enc(id)}`),
      /** Valide et envoie : rend l'e-mail « sent », ou « failed » avec error_message. */
      approve: (id: string) => this.request<EmailDetail>('POST', `/api/admin/email-outbox/${enc(id)}/approve`),
      reject: (id: string, reason = '') =>
        this.request<EmailDetail>('POST', `/api/admin/email-outbox/${enc(id)}/reject`, { body: { reason } }),
      approveMany: (ids: string[]) =>
        this.request<{ approved: number; failed: number }>('POST', '/api/admin/email-outbox/bulk-approve', { body: { ids } }),
      rejectMany: (ids: string[], reason = '') =>
        this.request<{ rejected: number; failed: number }>('POST', '/api/admin/email-outbox/bulk-reject', { body: { ids, reason } }),
    },
  }

  // ── Temps réel ───────────────────────────────────────────────────────

  /** S'abonne aux événements ; la fonction rendue arrête le flux.
   *  onStatus : le flux est-il ouvert (indicateur « en direct »). */
  subscribe(onEvent: (e: RealtimeEvent) => void, onReconnect?: () => void, onStatus?: (live: boolean) => void): () => void {
    let opened = false
    return streamSse({
      open: async (signal) =>
        this.fetcher(`${this.baseUrl}/api/events`, { headers: await this.authHeaders(), signal }),
      onOpen: () => {
        if (opened) onReconnect?.()
        opened = true
        onStatus?.(true)
      },
      onDown: () => onStatus?.(false),
      onMessage: (m) => {
        try {
          onEvent(JSON.parse(m.data) as RealtimeEvent)
        } catch {
          // message illisible : ignoré
        }
      },
    })
  }
}

function enc(s: string): string {
  return encodeURIComponent(s)
}

export function createClient(opts: ClientOptions): FeedbaacksClient {
  return new FeedbaacksClient(opts)
}
